"use client";

import React, { useMemo, useState } from "react";
import dynamic from "next/dynamic";
import { useParams } from "next/navigation";
import { useLocale } from "next-intl";
import { Button, Stack } from "@mui/material";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { RADIUS } from "src/themes/tokens";
import { formatSize } from "src/components/ar-preview/ar-maths";
import {
  analyticsSize,
  arArtworkLink,
  arCopyKey,
  arSizeLine,
  artworkArProduct,
} from "src/components/ar-preview/ar-product";
import type { Artwork } from "./artwork-types";
import type { OfferedSize } from "./artwork-sizing";
import { artworkArImageUrl } from "./useArtwork";

/** three.js and model-viewer stay out of the catalogue page until the button is pressed. */
const ArPreviewSheet = dynamic(() => import("src/components/ar-preview/ArPreviewSheet"), { ssr: false });

/** The finish values the locale files name (editor.canvasMaterial_* / canvasMount_*). */
const MATERIALS = ["canvas", "paper"];
const MOUNTS = ["stretched", "rolled"];

/**
 * "Poglejte na svoji steni" on an art or photography page: the chosen size
 * and finish of this piece, in AR, at its real size.
 *
 * The piece is printed whole and centred to the print's shape (the server
 * renders `fit: cover` with a full crop), which is what the AR face shows:
 * no crop is passed, so build-model takes the same centre crop. The size
 * gates (aspect tolerance, dpi floor) are the page's: only an offered size
 * reaches this button.
 */
export default function ArtworkArButton({
  kind,
  artwork,
  size,
}: {
  kind: "art" | "photography";
  artwork: Pick<Artwork, "slug" | "updatedAt">;
  size: OfferedSize | null | undefined;
}) {
  const t = useAppTranslate();
  const params = useParams();
  const intlLocale = useLocale();
  const locale = (params?.locale as string | undefined) ?? intlLocale;
  const [open, setOpen] = useState(false);
  // Mounted from the first open on, so closing animates and focus returns.
  const [mounted, setMounted] = useState(false);

  const ar = useMemo(() => {
    if (!size) return null;
    const av = (size.variant.axisValues ?? {}) as Record<string, string>;
    const material = av.material ?? null;
    const mount = av.mount ?? null;
    const product = artworkArProduct({
      material,
      mount,
      widthMm: size.widthMm,
      heightMm: size.heightMm,
      bleedMm: size.variant.printSpec?.bleedMm,
    });
    return { product, material, mount };
  }, [size]);

  const sizeLine = ar
    ? arSizeLine({
        productName: t(`arPreview.product_${kind}`),
        size: formatSize(ar.product.widthCm, ar.product.heightCm),
        finish: [
          ar.material && MATERIALS.includes(ar.material) && t(`editor.canvasMaterial_${ar.material}`),
          ar.mount && MOUNTS.includes(ar.mount) && t(`editor.canvasMount_${ar.mount}`),
        ],
      })
    : "";

  const analytics = useMemo(
    () =>
      ar
        ? {
            size: analyticsSize(ar.product),
            material: ar.material,
            mount: ar.mount,
            source: "product-page" as const,
            product: kind,
          }
        : null,
    [ar, kind],
  );

  // The picture is ours and public, so the phone's link is permanent.
  const deepLink = useMemo(
    () =>
      ar && open
        ? arArtworkLink({
            origin: window.location.origin,
            locale,
            kind,
            slug: artwork.slug,
            product: ar.product,
            material: ar.material,
            mount: ar.mount,
          })
        : null,
    [ar, open, locale, kind, artwork.slug],
  );

  const imageSrc = useMemo(
    () => artworkArImageUrl(artwork.slug, artwork.updatedAt ? String(artwork.updatedAt) : undefined),
    [artwork.slug, artwork.updatedAt],
  );

  if (!ar || !analytics) return null;

  return (
    <>
      <Stack alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="outlined"
          size="large"
          startIcon={<ViewInArOutlinedIcon />}
          onClick={() => {
            setMounted(true);
            setOpen(true);
          }}
          data-testid={`${kind}-ar-open`}
          sx={{
            minHeight: 48,
            px: 3,
            fontSize: 16,
            fontWeight: 700,
            textTransform: "none",
            borderWidth: 2,
            borderRadius: `${RADIUS.card}px`,
            "&:hover": { borderWidth: 2 },
          }}
        >
          {t(arCopyKey(kind, "viewOnWall"))}
        </Button>
      </Stack>
      {mounted && (
        <ArPreviewSheet
          open={open}
          onClose={() => setOpen(false)}
          imageSrc={imageSrc}
          imageTrusted
          crop={null}
          product={ar.product}
          sizeLine={sizeLine}
          deepLink={deepLink}
          analytics={analytics}
        />
      )}
    </>
  );
}
